import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Paper, TextField, Typography } from '@mui/material';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';

const InitialPasswordSetup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');
    setSuccess(false);

    if (!token) {
      setMessage('This password-setup link is missing or invalid. Please request a new approval email.');
      return;
    }
    if (newPassword.length < 8) {
      setMessage('Your password must contain at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage('The passwords do not match.');
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/set-initial-password`,
        { token, newPassword }
      );
      setMessage(response.data?.message || 'Password created successfully. You can now sign in.');
      setSuccess(true);
      window.setTimeout(() => navigate('/2.0/login', { replace: true }), 1800);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Unable to create your password right now.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '68vh', display: 'grid', placeItems: 'center', px: 2, py: 5 }}>
      <Paper elevation={4} sx={{ width: '100%', maxWidth: 500, p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
        <Typography variant="overline" sx={{ color: '#0f4cbb', fontWeight: 800, letterSpacing: '.08em' }}>
          Udyog Connect account setup
        </Typography>
        <Typography variant="h4" sx={{ mt: 0.5, mb: 1.25, fontWeight: 800 }}>
          Create your password
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Your approval is confirmed. Choose a password to activate your account. This secure setup link expires after 24 hours and can only be used once.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            fullWidth
            required
            margin="normal"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            helperText="At least 8 characters"
          />
          <TextField
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            fullWidth
            required
            margin="normal"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
          />
          <Button type="submit" variant="contained" size="large" fullWidth sx={{ mt: 2 }} disabled={submitting}>
            {submitting ? 'Creating password…' : 'Activate account and sign in'}
          </Button>
        </Box>

        {message && <Alert severity={success ? 'success' : 'error'} sx={{ mt: 2 }}>{message}</Alert>}
      </Paper>
    </Box>
  );
};

export default InitialPasswordSetup;
